import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import { criadorTipoAtendimentoStream } from '../../../services/prompt';
import { getTiposResumos } from '../../../services/resumo';

function MarkdownAssistant({ content, color }) {
    const theme = useTheme();
    return (
        <ReactMarkdown
            components={{
                p: ({ children }) => (
                    <Typography variant="body2" color={color} sx={{ mb: 0.5, '&:last-child': { mb: 0 } }}>
                        {children}
                    </Typography>
                ),
                strong: ({ children }) => (
                    <Box component="strong" sx={{ fontWeight: 700 }}>
                        {children}
                    </Box>
                ),
                em: ({ children }) => (
                    <Box component="em" sx={{ fontStyle: 'italic' }}>
                        {children}
                    </Box>
                ),
                ul: ({ children }) => (
                    <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                        {children}
                    </Box>
                ),
                ol: ({ children }) => (
                    <Box component="ol" sx={{ pl: 2, my: 0.5 }}>
                        {children}
                    </Box>
                ),
                li: ({ children }) => (
                    <Typography component="li" variant="body2" color={color}>
                        {children}
                    </Typography>
                ),
                h1: ({ children }) => (
                    <Typography variant="subtitle1" fontWeight={700} color={color} sx={{ mt: 1, mb: 0.5 }}>
                        {children}
                    </Typography>
                ),
                h2: ({ children }) => (
                    <Typography variant="subtitle2" fontWeight={700} color={color} sx={{ mt: 1, mb: 0.5 }}>
                        {children}
                    </Typography>
                ),
                h3: ({ children }) => (
                    <Typography variant="body2" fontWeight={700} color={color} sx={{ mt: 0.5, mb: 0.25 }}>
                        {children}
                    </Typography>
                ),
                code: ({ inline, children }) =>
                    inline ? (
                        <Box
                            component="code"
                            sx={{
                                backgroundColor: 'rgba(0,0,0,0.08)',
                                borderRadius: 0.5,
                                px: 0.5,
                                fontFamily: 'monospace',
                                fontSize: '0.8em',
                            }}
                        >
                            {children}
                        </Box>
                    ) : (
                        <Box
                            component="pre"
                            sx={{
                                backgroundColor: 'rgba(0,0,0,0.06)',
                                borderRadius: 1,
                                p: 1,
                                overflowX: 'auto',
                                fontSize: '0.8em',
                                fontFamily: 'monospace',
                                my: 0.5,
                            }}
                        >
                            <code>{children}</code>
                        </Box>
                    ),
                blockquote: ({ children }) => (
                    <Box
                        component="blockquote"
                        sx={{
                            borderLeft: `3px solid ${theme.palette.divider}`,
                            pl: 1,
                            ml: 0,
                            my: 0.5,
                            opacity: 0.8,
                        }}
                    >
                        {children}
                    </Box>
                ),
            }}
        >
            {content}
        </ReactMarkdown>
    );
}

function CriadorTipoAtendimento({ open, onClose, onPromptCriado, modeloBase }) {
    const theme = useTheme();
    const [mensagens, setMensagens] = useState([]);
    const [input, setInput] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [erro, setErro] = useState(null);
    const [concluido, setConcluido] = useState(false);
    const xsRef = useRef(null);
    const messagesEndRef = useRef(null);
    const assistantBufferRef = useRef('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setMensagens([]);
            setInput('');
            setErro(null);
            setConcluido(false);
            assistantBufferRef.current = '';
            iniciarConversa();
        } else {
            if (xsRef.current) {
                xsRef.current.close();
                xsRef.current = null;
            }
        }
    }, [open]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensagens]);

    function iniciarConversa() {
        const conteudoInicial = modeloBase
            ? `Olá, quero criar um novo tipo de atendimento baseado no modelo "${modeloBase.name}".`
            : 'Olá, quero criar um novo tipo de atendimento.';
        const mensagensIniciais = [{ role: 'user', content: conteudoInicial }];
        setMensagens([{ role: 'user', content: conteudoInicial }]);
        chamarStream(mensagensIniciais);
    }

    function chamarStream(historico) {
        setStreaming(true);
        assistantBufferRef.current = '';

        setMensagens((prev) => [...prev, { role: 'assistant', content: '' }]);

        const xs = criadorTipoAtendimentoStream(historico, modeloBase?.id, {
            onText: (chunk) => {
                assistantBufferRef.current += chunk;
                setMensagens((prev) => {
                    const copia = [...prev];
                    copia[copia.length - 1] = {
                        role: 'assistant',
                        content: assistantBufferRef.current,
                    };
                    return copia;
                });
            },
            onPromptCriado: (data) => {
                setConcluido(true);
                setStreaming(false);
                if (data) {
                    onPromptCriado && onPromptCriado(data);
                }
            },
            onError: (msg) => {
                setErro(msg || 'Ocorreu um erro. Tente novamente.');
                setStreaming(false);
            },
            onClose: () => {
                setStreaming(false);
                if (inputRef.current) inputRef.current.focus();
            },
        });

        xsRef.current = xs;
    }

    function enviarMensagem() {
        if (!input.trim() || streaming) return;

        const novaMensagem = { role: 'user', content: input.trim() };
        const novoHistorico = [...mensagens.filter((m) => m.content !== ''), novaMensagem];

        setMensagens((prev) => [...prev.filter((m) => m.content !== ''), novaMensagem]);
        setInput('');
        setErro(null);
        chamarStream(novoHistorico);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            enviarMensagem();
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '80vh' } }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    pb: 1,
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={600} color={theme.palette.primary.dark}>
                        Criar novo tipo de atendimento
                    </Typography>
                    {modeloBase && (
                        <Typography variant="caption" color={theme.palette.text.secondary}>
                            Baseado em: {modeloBase.name}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    p: 0,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        px: 2,
                        py: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    {mensagens.map((msg, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                            }}
                        >
                            <Box
                                sx={{
                                    maxWidth: '80%',
                                    px: 2,
                                    py: 1,
                                    borderRadius: 2,
                                    backgroundColor:
                                        msg.role === 'user'
                                            ? theme.palette.primary.main
                                            : theme.palette.grey[100],
                                    color:
                                        msg.role === 'user'
                                            ? theme.palette.primary.contrastText
                                            : theme.palette.text.primary,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {msg.content === '' && msg.role === 'assistant' ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : msg.role === 'assistant' ? (
                                    <MarkdownAssistant
                                        content={msg.content}
                                        color={theme.palette.text.primary}
                                    />
                                ) : (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {msg.content}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}

                    {concluido && (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 2,
                                color: theme.palette.success.main,
                            }}
                        >
                            <Typography variant="body2" fontWeight={600}>
                                Tipo de atendimento criado com sucesso!
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                sx={{ mt: 1 }}
                                onClick={onClose}
                            >
                                Fechar
                            </Button>
                        </Box>
                    )}

                    {erro && (
                        <Typography variant="caption" color="error" sx={{ textAlign: 'center', py: 1 }}>
                            {erro}
                        </Typography>
                    )}

                    <div ref={messagesEndRef} />
                </Box>

                {!concluido && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 1,
                            px: 2,
                            py: 1.5,
                            borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <TextField
                            inputRef={inputRef}
                            fullWidth
                            multiline
                            maxRows={4}
                            size="small"
                            placeholder="Digite sua resposta..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={streaming}
                        />
                        <IconButton
                            color="primary"
                            onClick={enviarMensagem}
                            disabled={!input.trim() || streaming}
                        >
                            {streaming ? <CircularProgress size={20} /> : <SendIcon />}
                        </IconButton>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default CriadorTipoAtendimento;
